<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button
          v-if="activeTab === 'general'"
          type="button"
          class="btn btn-primary"
          :disabled="savingGeneral"
          @click="handleSaveGeneral"
        >
          <span v-if="savingGeneral">儲存中...</span>
          <span v-else>儲存一般設定</span>
        </button>
        <button
          v-if="activeTab === 'ecpay'"
          type="button"
          class="btn btn-primary"
          :disabled="savingEcpay"
          @click="handleSaveEcpay"
        >
          <span v-if="savingEcpay">儲存中...</span>
          <span v-else>儲存 ECPay 設定</span>
        </button>
        <button
          v-if="activeTab === 'every8d'"
          type="button"
          class="btn btn-primary"
          :disabled="savingEvery8d"
          @click="handleSaveEvery8d"
        >
          <span v-if="savingEvery8d">儲存中...</span>
          <span v-else>儲存 Every8d 設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchSettings">重試</button>
    </div>

    <template v-else>
      <!-- 頁籤 -->
      <div class="tabs-bar">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'general' }"
          @click="activeTab = 'general'"
        >
          <i class="ri-settings-3-line"></i>
          一般設定
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'ecpay' }"
          @click="activeTab = 'ecpay'"
        >
          <i class="ri-bank-card-line"></i>
          ECPay 金流設定
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'every8d' }"
          @click="activeTab = 'every8d'"
        >
          <i class="ri-message-2-line"></i>
          Every8d 簡訊
        </button>
      </div>

      <!-- 一般設定 -->
      <div v-if="activeTab === 'general'" class="settings-container">
        <form @submit.prevent="handleSaveGeneral">
          <div class="form-section">
            <h2>ChatMate 成本加價設定</h2>
            <p class="section-description">設定系統層級的成本加價率，會套用到所有客戶的 ChatMate 對話成本計算</p>

            <div class="form-group">
              <label>成本加價率（%） <span class="required">*</span></label>
              <input
                v-model.number="formData.cost_markup_rate"
                type="number"
                step="0.01"
                min="0"
                max="1000"
                class="form-control"
                placeholder="0.00"
                required
              />
              <small class="form-hint">
                <strong>效果說明：</strong>設定成本加價率，系統會在 OpenAI 實際成本上加上此百分比作為營利。
                <ul>
                  <li><strong>範例：</strong>如果設定為 20，表示在 OpenAI 成本上加價 20%</li>
                  <li><strong>計算方式：</strong>最終成本 = OpenAI 成本 × (1 + 加價率 / 100)</li>
                  <li><strong>預設值：</strong>0（不加價，顯示 OpenAI 實際成本）</li>
                  <li><strong>注意：</strong>此設定會影響所有客戶的對話記錄成本計算</li>
                </ul>
              </small>
            </div>
          </div>

          <div class="form-section">
            <h2>匯率設定</h2>
            <p class="section-description">設定 USD 到 TWD（台幣）的匯率，用於成本換算顯示</p>

            <div class="form-group">
              <label>USD 到 TWD 匯率 <span class="required">*</span></label>
              <div class="rate-input-row">
                <input
                  v-model.number="formData.usd_to_twd_rate"
                  type="number"
                  step="0.001"
                  min="0.001"
                  max="1000"
                  class="form-control"
                  placeholder="32.5"
                  required
                />
                <button
                  type="button"
                  class="btn btn-outline-primary btn-fetch-cbc"
                  :disabled="fetchingRate"
                  @click="fetchRateFromCbc"
                >
                  <span v-if="fetchingRate">取得中...</span>
                  <span v-else>從央行 Open Data 更新</span>
                </button>
              </div>
              <small class="form-hint">
                <strong>效果說明：</strong>設定 USD 到 TWD 的匯率，用於將 OpenAI 成本（USD）換算為台幣顯示。
                <ul>
                  <li><strong>範例：</strong>如果設定為 32.5，表示 1 USD = 32.5 TWD</li>
                  <li><strong>計算方式：</strong>台幣金額 = USD 金額 × 匯率</li>
                  <li><strong>預設值：</strong>32.5</li>
                  <li><strong>注意：</strong>此設定會影響所有成本顯示（對話記錄、統計等）</li>
                  <li><strong>建議：</strong>可點「從央行 Open Data 更新」取得中央銀行新台幣/美元銀行間收盤匯率（日資料），並自動寫入系統</li>
                </ul>
              </small>
            </div>
          </div>

          <div class="form-section">
            <h2>管理員通知設定</h2>
            <p class="section-description">統一管理官網與訂閱通知的收件人，也可以為各事件指定不同名單。原有收件人會保留為自訂名單。</p>
            <div class="form-group">
              <label for="admin-notification-emails">共用管理員收件人 Email</label>
              <textarea id="admin-notification-emails" v-model="formData.adminNotifications.emails" class="form-control" rows="3" placeholder="admin@example.com, finance@example.com"></textarea>
              <small class="form-hint">每份名單最多 20 個 Email，以逗號、分號或換行分隔。留空不寄送使用該名單的管理員通知。</small>
            </div>
            <div v-for="event in notificationEvents" :key="event.key" class="form-group notification-event">
              <label class="notification-checkbox">
                <input type="checkbox" v-model="formData.adminNotifications.events[event.key].enabled" />
                {{ event.label }}
              </label>
              <p class="form-hint">{{ event.description }}</p>
              <template v-if="formData.adminNotifications.events[event.key].enabled">
                <label class="toggle-label notification-switch">
                  <span>使用共用管理員收件人</span>
                  <span class="switch">
                    <input type="checkbox" v-model="formData.adminNotifications.events[event.key].useShared" />
                    <span class="slider"></span>
                  </span>
                </label>
                <template v-if="!formData.adminNotifications.events[event.key].useShared">
                  <label :for="'notify-' + event.key">{{ event.label }}自訂收件人 Email</label>
                  <textarea :id="'notify-' + event.key" v-model="formData.adminNotifications.events[event.key].emails" class="form-control" rows="2" placeholder="自訂 Email，留空不寄送此事件的管理員通知"></textarea>
                </template>
              </template>
            </div>
          </div>
        </form>
      </div>

      <!-- ECPay 金流設定 -->
      <div v-if="activeTab === 'ecpay'" class="settings-container">
        <form @submit.prevent="handleSaveEcpay">
          <div class="form-section">
            <h2>ECPay 金流設定</h2>
            <p class="section-description">設定綠界金流（ECPay）的串接參數，用於客戶訂閱與加購方案的定期定額扣款</p>

            <div class="form-group">
              <label>運行模式</label>
              <div class="radio-group">
                <label class="radio-label">
                  <input type="radio" v-model="formData.ecpay_mode" value="sandbox" />
                  <span>測試環境（Sandbox）</span>
                </label>
                <label class="radio-label">
                  <input type="radio" v-model="formData.ecpay_mode" value="production" />
                  <span>正式環境（Production）</span>
                </label>
              </div>
              <small class="form-hint">
                測試環境 URL：<code>https://payment-stage.ecpay.com.tw</code><br>
                正式環境 URL：<code>https://payment.ecpay.com.tw</code>
              </small>
              <div v-if="formData.ecpay_mode === 'sandbox'" class="sandbox-autofill sandbox-autofill-payment">
                <small class="form-hint block-hint">
                  綠界全方位金流測試特店（公開測試用，勿用於正式收款）。選擇測試環境且三欄為空時會自動帶入；亦可手動點下方按鈕。
                </small>
                <button type="button" class="btn btn-auto-gen" @click="autoFillPaymentSandbox">
                  <i class="ri-flask-line"></i> 自動填入 Sandbox 測試資料（金流）
                </button>
              </div>
            </div>

            <div class="form-group">
              <label>特店編號 (MerchantID) <span class="required">*</span></label>
              <input
                v-model="formData.ecpay_merchant_id"
                type="text"
                class="form-control"
                placeholder="請輸入綠界特店編號"
              />
            </div>

            <div class="form-group">
              <label>HashKey <span class="required">*</span></label>
              <div class="input-with-toggle">
                <input
                  v-model="formData.ecpay_hash_key"
                  :type="showHashKey ? 'text' : 'password'"
                  class="form-control"
                  placeholder="請輸入 HashKey"
                  autocomplete="off"
                />
                <button type="button" class="btn-toggle-visibility" @click="showHashKey = !showHashKey" :title="showHashKey ? '隱藏' : '顯示'">
                  <i :class="showHashKey ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </div>

            <div class="form-group">
              <label>HashIV <span class="required">*</span></label>
              <div class="input-with-toggle">
                <input
                  v-model="formData.ecpay_hash_iv"
                  :type="showHashIV ? 'text' : 'password'"
                  class="form-control"
                  placeholder="請輸入 HashIV"
                  autocomplete="off"
                />
                <button type="button" class="btn-toggle-visibility" @click="showHashIV = !showHashIV" :title="showHashIV ? '隱藏' : '顯示'">
                  <i :class="showHashIV ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </div>

            <div class="info-box ecpay-webhook-info">
              <p class="ecpay-webhook-info-title">
                <i class="ri-information-line" aria-hidden="true"></i>
                ReturnURL 與 PeriodReturnURL（綠界 Server POST）
              </p>
              <ul>
                <li>
                  <strong>ReturnURL</strong>：首期授權或<strong>第一次</strong>扣款／付款結果。綠界<strong>伺服器</strong>會 POST 到此前網址；本系統對應路由為
                  <code>POST /api/v1/webhooks/ecpay/return</code>。
                </li>
                <li>
                  <strong>PeriodReturnURL</strong>：自<strong>第 2 期</strong>起每次定期扣款結果。綠界<strong>伺服器</strong>會 POST 到此前網址；本系統對應路由為
                  <code>POST /api/v1/webhooks/ecpay/period</code>。
                </li>
                <li>
                  兩者皆為<strong>後端 Webhook</strong>（與下方 ClientBackURL：使用者瀏覽器付款後導回前端的網址不同）。綠界後台若另有「通知網址」欄位，請填<strong>完整 URL</strong>，且須與實際對外可連線的 API 網域一致。
                </li>
                <li>
                  完整網址為「API 網域 + 路徑」。常見範例：本機
                  <code>http://localhost:3000</code>、測試環境
                  <code>https://dev-api.lockline.io</code>、正式環境
                  <code>https://api.lockline.io</code>（實際以部署時的環境變數
                  <code>API_BASE_URL</code> 為準）。若此處未填寫且後端已設定
                  <code>API_BASE_URL</code>，建立訂單時會自動帶入上述兩條路徑。
                </li>
              </ul>
            </div>

            <div class="form-group">
              <label>ReturnURL（首期授權／付款結果，Server POST）</label>
              <div class="url-input-row">
                <input
                  v-model="formData.ecpay_return_url"
                  type="url"
                  class="form-control"
                  :placeholder="suggestedReturnWebhookUrl"
                />
                <button type="button" class="btn btn-auto-gen" @click="autoFillReturnUrlSuggested" title="只填入此欄（建議路由）">
                  帶入建議值
                </button>
              </div>
              <small class="form-hint">
                後端路由 <code>POST …/webhooks/ecpay/return</code>。建立訂單時傳給綠界的 <code>ReturnURL</code>：僅<strong>第一期</strong>會 POST 到此網址。建議：<code>{{ suggestedReturnWebhookUrl }}</code>
              </small>
            </div>

            <div class="form-group">
              <label>PeriodReturnURL（第 2 期起定期扣款結果，Server POST）</label>
              <div class="url-input-row">
                <input
                  v-model="formData.ecpay_notify_url"
                  type="url"
                  class="form-control"
                  :placeholder="suggestedPeriodWebhookUrl"
                />
                <button type="button" class="btn btn-auto-gen" @click="autoFillNotifyUrlSuggested" title="只填入此欄（建議路由）">
                  帶入建議值
                </button>
              </div>
              <small class="form-hint">
                後端路由 <code>POST …/webhooks/ecpay/period</code>。建立訂單時傳給綠界的 <code>PeriodReturnURL</code>：<strong>第 2 期起</strong>每次扣款 POST 到此網址。建議：<code>{{ suggestedPeriodWebhookUrl }}</code>
              </small>
            </div>

            <div class="form-group form-group-compact">
              <button type="button" class="btn btn-secondary btn-sm" @click="autoFillBothWebhookRoutes">
                一次帶入兩條建議路由（return + period）
              </button>
              <small class="form-hint block-hint">
                後端為 <code>POST …/webhooks/ecpay/return</code>（首期）與 <code>POST …/webhooks/ecpay/period</code>（續扣）兩支路由；綠界後台須分別填寫完整 URL。
              </small>
            </div>

            <div class="form-group">
              <label>ClientBackURL（付款完成後瀏覽器返回頁面）</label>
              <div class="url-input-row">
                <input
                  v-model="formData.ecpay_client_back_url"
                  type="url"
                  class="form-control"
                  :placeholder="`${suggestedClientBackUrl}`"
                />
                <button type="button" class="btn btn-auto-gen" @click="autoFillClientBackUrl" title="自動產生">
                  自動產生
                </button>
              </div>
              <small class="form-hint">
                客戶在綠界付款完成後，<strong>瀏覽器</strong>自動跳回的前端頁面。<br>
                目前建議值：<code>{{ suggestedClientBackUrl }}</code>
              </small>
            </div>
          </div>

          <!-- 電子發票設定 -->
          <div class="form-section invoice-section">
            <h2>電子發票設定</h2>
            <p class="section-description">設定綠界電子發票（B2C）串接參數。啟用後，每筆成功扣款將自動開立電子發票並寄送至客戶 Email。</p>

            <div class="form-group">
              <label class="toggle-label">
                <span>啟用電子發票</span>
                <label class="switch">
                  <input type="checkbox" v-model="formData.ecpay_invoice_enabled" />
                  <span class="slider"></span>
                </label>
              </label>
              <small class="form-hint">關閉時不會呼叫電子發票 API，所有交易將顯示「未開立」狀態</small>
            </div>

            <template v-if="formData.ecpay_invoice_enabled">
              <div class="info-box">
                <i class="ri-information-line"></i>
                電子發票的 MerchantID、HashKey、HashIV 與金流不同，請至綠界後台「電子發票管理」取得。
                測試環境端點：<code>https://einvoice-stage.ecpay.com.tw</code>
                <div v-if="formData.ecpay_mode === 'sandbox'" class="sandbox-autofill">
                  <button type="button" class="btn btn-auto-gen" @click="autoFillInvoiceSandbox">
                    <i class="ri-flask-line"></i> 自動填入 Sandbox 測試資料
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label>電子發票特店編號</label>
                <input
                  v-model="formData.ecpay_invoice_merchant_id"
                  type="text"
                  class="form-control"
                  placeholder="請輸入電子發票特店編號"
                />
              </div>

              <div class="form-group">
                <label>電子發票 HashKey</label>
                <div class="input-with-toggle">
                  <input
                    v-model="formData.ecpay_invoice_hash_key"
                    :type="showInvoiceHashKey ? 'text' : 'password'"
                    class="form-control"
                    placeholder="請輸入電子發票 HashKey"
                    autocomplete="off"
                  />
                  <button type="button" class="btn-toggle-visibility" @click="showInvoiceHashKey = !showInvoiceHashKey">
                    <i :class="showInvoiceHashKey ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label>電子發票 HashIV</label>
                <div class="input-with-toggle">
                  <input
                    v-model="formData.ecpay_invoice_hash_iv"
                    :type="showInvoiceHashIV ? 'text' : 'password'"
                    class="form-control"
                    placeholder="請輸入電子發票 HashIV"
                    autocomplete="off"
                  />
                  <button type="button" class="btn-toggle-visibility" @click="showInvoiceHashIV = !showInvoiceHashIV">
                    <i :class="showInvoiceHashIV ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
              </div>
            </template>
          </div>
        </form>
      </div>

      <div v-if="activeTab === 'every8d'" class="settings-container">
        <form @submit.prevent="handleSaveEvery8d">
          <div class="form-section">
            <h2>Every8d 簡訊</h2>
            <p class="section-description">全平台共用一組 Every8d 帳號。帳密存於系統設定；若帳號與密碼都空白，發送時沿用 Secrets Manager。</p>
            <div class="form-group">
              <label>帳號</label>
              <input v-model="formData.every8d_uid" type="text" class="form-control" autocomplete="off" />
            </div>
            <div class="form-group">
              <label>密碼</label>
              <div class="input-with-toggle">
                <input
                  v-model="formData.every8d_pwd"
                  :type="showEvery8dPwd ? 'text' : 'password'"
                  class="form-control"
                  autocomplete="off"
                />
                <button type="button" class="btn-toggle-visibility" @click="showEvery8dPwd = !showEvery8dPwd">
                  <i :class="showEvery8dPwd ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </div>
            <div class="form-group">
              <label>簡訊主旨</label>
              <input v-model="formData.every8d_sb" type="text" class="form-control" placeholder="LOCKLINE" />
              <small class="form-hint">不會出現在簡訊內文，只供 Every8d 後台註記。留空時使用 LOCKLINE。</small>
            </div>
            <div class="sms-point-board">
              <div class="sms-point-card">
                <span class="sms-point-label">共用帳號剩餘點數</span>
                <strong class="sms-point-value">{{ every8dCredit == null ? '—' : formatSmsPoints(every8dCredit) }}</strong>
              </div>
              <div class="sms-point-card">
                <span class="sms-point-label">客戶未使用點數</span>
                <strong class="sms-point-value">{{ every8dAllocated == null ? '—' : formatSmsPoints(every8dAllocated) }}</strong>
              </div>
              <div class="sms-point-card is-dispatch" :class="{ 'is-empty': every8dDispatchable != null && Number(every8dDispatchable) <= 0 }">
                <span class="sms-point-label">剩餘可派點數</span>
                <strong class="sms-point-value">{{ every8dDispatchable == null ? '—' : formatSmsPoints(every8dDispatchable) }}</strong>
              </div>
            </div>
            <div class="form-group">
              <button type="button" class="btn btn-outline-primary" :disabled="loadingEvery8dCredit" @click="fetchEvery8dCredit">
                {{ loadingEvery8dCredit ? '查詢中...' : '重新查詢' }}
              </button>
              <small class="form-hint">剩餘可派點數 = 共用帳號剩餘點數 − 客戶未使用點數。</small>
            </div>
          </div>
        </form>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { showError, showSuccess } from '@/utils/swal.js';
import apiClient from '@/api/axios.js';
import { formatSmsPoints } from '@/utils/smsPointLabels.js';

export default {
  name: 'SystemSettings',
  setup() {
    const { pageTitle } = usePageTitle('系統設定');
    const loading = ref(false);
    const savingGeneral = ref(false);
    const savingEcpay = ref(false);
    const fetchingRate = ref(false);
    const error = ref(null);
    const activeTab = ref('general');
    const showHashKey = ref(false);
    const showHashIV = ref(false);
    const showInvoiceHashKey = ref(false);
    const showInvoiceHashIV = ref(false);

    const suggestedEcpayApiOrigin = computed(() => {
      const apiBase = process.env.VUE_APP_API_BASE_URL || '';
      if (apiBase && !apiBase.startsWith('/')) {
        return apiBase.replace(/\/api\/v1\/?$/, '');
      }
      // 開發環境常為 /api/v1（由 admin proxy 至本機 API）；Webhook 須為後端網域而非 admin origin
      if (apiBase.startsWith('/')) {
        return 'http://localhost:3000';
      }
      return window.location.origin;
    });
    const suggestedReturnWebhookUrl = computed(
      () => `${suggestedEcpayApiOrigin.value}/api/v1/webhooks/ecpay/return`
    );
    const suggestedPeriodWebhookUrl = computed(
      () => `${suggestedEcpayApiOrigin.value}/api/v1/webhooks/ecpay/period`
    );

    const suggestedClientBackUrl = computed(() => `${window.location.origin}/client/subscription`);

    const autoFillReturnUrlSuggested = () => {
      formData.ecpay_return_url = suggestedReturnWebhookUrl.value;
    };
    const autoFillNotifyUrlSuggested = () => {
      formData.ecpay_notify_url = suggestedPeriodWebhookUrl.value;
    };
    const autoFillBothWebhookRoutes = () => {
      formData.ecpay_return_url = suggestedReturnWebhookUrl.value;
      formData.ecpay_notify_url = suggestedPeriodWebhookUrl.value;
    };
    const autoFillClientBackUrl = () => { formData.ecpay_client_back_url = suggestedClientBackUrl.value; };

    /** 綠界全方位金流 Stage 測試特店（與官方文件／api/.env.example 一致） */
    const ECPAY_SANDBOX_PAYMENT = {
      merchantId: '2000132',
      hashKey: '5294y06JbISpM5x9',
      hashIv: 'v77hoKGq4kWxNNIS'
    };

    const autoFillPaymentSandbox = () => {
      formData.ecpay_merchant_id = ECPAY_SANDBOX_PAYMENT.merchantId;
      formData.ecpay_hash_key = ECPAY_SANDBOX_PAYMENT.hashKey;
      formData.ecpay_hash_iv = ECPAY_SANDBOX_PAYMENT.hashIv;
    };

    const paymentSandboxFieldsEmpty = () =>
      !(formData.ecpay_merchant_id || '').trim() &&
      !(formData.ecpay_hash_key || '').trim() &&
      !(formData.ecpay_hash_iv || '').trim();

    const applyPaymentSandboxDefaultsIfNeeded = () => {
      if (formData.ecpay_mode !== 'sandbox') return;
      if (!paymentSandboxFieldsEmpty()) return;
      autoFillPaymentSandbox();
    };

    const autoFillInvoiceSandbox = () => {
      formData.ecpay_invoice_merchant_id = '2000132';
      formData.ecpay_invoice_hash_key = 'ejCk326UnaZWKisg';
      formData.ecpay_invoice_hash_iv = 'q9jcZX8Ib9LM8wYk';
    };

    const notificationEvents = [
      { key: 'trialRegistration', label: '免費試用註冊通知', description: '客戶完成 Email 驗證並啟用試用後，寄送公司、聯絡人、聯絡 Email 與試用到期日，不包含驗證連結。' },
      { key: 'contactInquiry', label: '聯絡我們通知', description: '官網聯絡表單成功送出後，通知管理員跟進。' },
      { key: 'subscriptionExpiry', label: '訂閱到期通知', description: '到期前 30 天、7 天及當天，以密件副本同步通知管理員；關閉不影響客戶原本的提醒。' },
    ];

    const formData = reactive({
      cost_markup_rate: 0.00,
      usd_to_twd_rate: 32.5,
      adminNotifications: {
        emails: '',
        events: Object.fromEntries(['trialRegistration', 'contactInquiry', 'subscriptionExpiry'].map(key => [key, { enabled: true, useShared: true, emails: '' }]))
      },
      ecpay_mode: 'sandbox',
      ecpay_merchant_id: '',
      ecpay_hash_key: '',
      ecpay_hash_iv: '',
      ecpay_return_url: '',
      ecpay_notify_url: '',
      ecpay_client_back_url: '',
      ecpay_invoice_enabled: false,
      ecpay_invoice_merchant_id: '',
      ecpay_invoice_hash_key: '',
      ecpay_invoice_hash_iv: '',
      every8d_uid: '',
      every8d_pwd: '',
      every8d_sb: ''
    });
    const savingEvery8d = ref(false);
    const loadingEvery8dCredit = ref(false);
    const every8dCredit = ref(null);
    const every8dAllocated = ref(null);
    const every8dDispatchable = ref(null);
    const showEvery8dPwd = ref(false);

    const fetchSettings = async () => {
      loading.value = true;
      error.value = null;

      try {
        const keys = [
          'chatmate_cost_markup_rate',
          'usd_to_twd_rate',
          'admin_notification_settings',
          'ecpay_mode',
          'ecpay_merchant_id',
          'ecpay_hash_key',
          'ecpay_hash_iv',
          'ecpay_return_url',
          'ecpay_notify_url',
          'ecpay_client_back_url',
          'ecpay_invoice_enabled',
          'ecpay_invoice_merchant_id',
          'ecpay_invoice_hash_key',
          'ecpay_invoice_hash_iv',
          'every8d_uid',
          'every8d_pwd',
          'every8d_sb'
        ];

        const responses = await Promise.allSettled(
          keys.map(key => apiClient.get(`/system/settings/${key}`))
        );

        const getValue = (res, fallback) => {
          if (res.status === 'fulfilled' && res.value?.success && res.value?.data) {
            return res.value.data.value ?? fallback;
          }
          return fallback;
        };

        formData.cost_markup_rate = parseFloat(getValue(responses[0], 0));
        formData.usd_to_twd_rate = parseFloat(getValue(responses[1], 32.5));
        const notificationResponse = responses[2];
        if (notificationResponse.status !== 'fulfilled' || !notificationResponse.value?.success || !notificationResponse.value?.data?.value?.events) {
          throw new Error('無法載入管理員通知設定，請重試');
        }
        formData.adminNotifications = notificationResponse.value.data.value;
        formData.ecpay_mode = getValue(responses[3], 'sandbox');
        formData.ecpay_merchant_id = getValue(responses[4], '');
        formData.ecpay_hash_key = getValue(responses[5], '');
        formData.ecpay_hash_iv = getValue(responses[6], '');
        formData.ecpay_return_url = getValue(responses[7], '');
        formData.ecpay_notify_url = getValue(responses[8], '');
        formData.ecpay_client_back_url = getValue(responses[9], '') || suggestedClientBackUrl.value;
        formData.ecpay_invoice_enabled = getValue(responses[10], 'false') === true || getValue(responses[10], 'false') === 'true';
        formData.ecpay_invoice_merchant_id = getValue(responses[11], '');
        formData.ecpay_invoice_hash_key = getValue(responses[12], '');
        formData.ecpay_invoice_hash_iv = getValue(responses[13], '');
        formData.every8d_uid = getValue(responses[14], '');
        formData.every8d_pwd = getValue(responses[15], '');
        formData.every8d_sb = getValue(responses[16], '');
      } catch (err) {
        console.error('獲取系統設定失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取系統設定失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const fetchRateFromCbc = async () => {
      fetchingRate.value = true;
      try {
        const res = await apiClient.post('/system/settings/usd_to_twd_rate/fetch-from-cbc');
        if (res?.success && res?.data) {
          formData.usd_to_twd_rate = parseFloat(res.data.value ?? 32.5);
          const dateHint = res.data.sourceDate
            ? `（資料日期：${res.data.sourceDate.slice(0, 4)}/${res.data.sourceDate.slice(4, 6)}/${res.data.sourceDate.slice(6, 8)}）`
            : '';
          await showSuccess('已更新', `匯率已從中央銀行 Open Data 更新為 ${formData.usd_to_twd_rate}${dateHint}`);
        } else {
          await showError('更新失敗', res?.message || '無法取得央行匯率');
        }
      } catch (err) {
        const msg =
          err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          '無法從央行 Open Data 取得匯率，請稍後再試或手動輸入匯率。';
        await showError('取得匯率失敗', msg);
      } finally {
        fetchingRate.value = false;
      }
    };

    const saveKeys = async (updates) => {
      const responses = await Promise.allSettled(
        updates.map(({ key, value, type }) =>
          apiClient.put(`/system/settings/${key}`, { value, type })
        )
      );
      const failed = responses.filter(r =>
        r.status === 'rejected' || (r.status === 'fulfilled' && !r.value?.success)
      );
      if (failed.length > 0) {
        const errorMessage =
          failed[0]?.reason?.response?.data?.message ||
          failed[0]?.value?.message ||
          '更新系統設定失敗';
        throw new Error(errorMessage);
      }
    };

    const handleSaveGeneral = async () => {
      const lists = [formData.adminNotifications.emails, ...Object.values(formData.adminNotifications.events).map(event => event.emails)];
      for (const list of lists) {
        const emails = [...new Set(list.split(/[,;\r\n]+/).map(email => email.trim().toLowerCase()).filter(Boolean))];
        if (emails.length > 20 || emails.some(email => email.length > 254 || !/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email))) {
          await showError('無法儲存', '請填寫有效的管理員 Email，每份名單最多 20 個');
          return;
        }
      }
      savingGeneral.value = true;
      try {
        await saveKeys([
          { key: 'chatmate_cost_markup_rate', value: formData.cost_markup_rate, type: 'number' },
          { key: 'usd_to_twd_rate', value: formData.usd_to_twd_rate, type: 'number' },
          { key: 'admin_notification_settings', value: formData.adminNotifications, type: 'json' }
        ]);
        await showSuccess('儲存成功', '一般設定已更新');
        await fetchSettings();
      } catch (err) {
        await showError('儲存失敗', err.message);
      } finally {
        savingGeneral.value = false;
      }
    };

    const handleSaveEvery8d = async () => {
      const uid = String(formData.every8d_uid || '').trim();
      const pwd = String(formData.every8d_pwd || '').trim();
      if ((uid && !pwd) || (!uid && pwd)) {
        await showError('無法儲存', '帳號與密碼要同時填寫，或同時留空以沿用 Secrets Manager');
        return;
      }
      savingEvery8d.value = true;
      try {
        await saveKeys([
          { key: 'every8d_uid', value: uid, type: 'string' },
          { key: 'every8d_pwd', value: pwd, type: 'string' },
          { key: 'every8d_sb', value: String(formData.every8d_sb || '').trim(), type: 'string' }
        ]);
        await showSuccess('儲存成功', 'Every8d 設定已更新');
        await fetchSettings();
      } catch (err) {
        await showError('儲存失敗', err.message);
      } finally {
        savingEvery8d.value = false;
      }
    };

    const fetchEvery8dCredit = async () => {
      loadingEvery8dCredit.value = true;
      try {
        const res = await apiClient.get('/system/settings/every8d/credit');
        every8dCredit.value = res?.data?.credit ?? null;
        every8dAllocated.value = res?.data?.allocated ?? null;
        every8dDispatchable.value = res?.data?.dispatchable ?? null;
      } catch (err) {
        every8dCredit.value = null;
        every8dAllocated.value = null;
        every8dDispatchable.value = null;
        await showError('查詢失敗', err.response?.data?.error?.message || err.response?.data?.message || err.message || '無法查詢 Every8d 剩餘點數');
      } finally {
        loadingEvery8dCredit.value = false;
      }
    };

    const handleSaveEcpay = async () => {
      const ret = (formData.ecpay_return_url || '').trim();
      const ntf = (formData.ecpay_notify_url || '').trim();
      if (!ret || !ntf) {
        await showError(
          '無法儲存',
          '請填寫 ReturnURL（首期，…/webhooks/ecpay/return）與 PeriodReturnURL（續扣，…/webhooks/ecpay/period）。可點「一次帶入兩條建議路由」。'
        );
        return;
      }
      savingEcpay.value = true;
      try {
        await saveKeys([
          { key: 'ecpay_mode', value: formData.ecpay_mode, type: 'string' },
          { key: 'ecpay_merchant_id', value: formData.ecpay_merchant_id, type: 'string' },
          { key: 'ecpay_hash_key', value: formData.ecpay_hash_key, type: 'string' },
          { key: 'ecpay_hash_iv', value: formData.ecpay_hash_iv, type: 'string' },
          { key: 'ecpay_return_url', value: formData.ecpay_return_url, type: 'string' },
          { key: 'ecpay_notify_url', value: formData.ecpay_notify_url, type: 'string' },
          { key: 'ecpay_client_back_url', value: formData.ecpay_client_back_url, type: 'string' },
          { key: 'ecpay_invoice_enabled', value: formData.ecpay_invoice_enabled, type: 'boolean' },
          { key: 'ecpay_invoice_merchant_id', value: formData.ecpay_invoice_merchant_id, type: 'string' },
          { key: 'ecpay_invoice_hash_key', value: formData.ecpay_invoice_hash_key, type: 'string' },
          { key: 'ecpay_invoice_hash_iv', value: formData.ecpay_invoice_hash_iv, type: 'string' }
        ]);
        await showSuccess('儲存成功', 'ECPay 金流設定已更新');
        await fetchSettings();
      } catch (err) {
        await showError('儲存失敗', err.message);
      } finally {
        savingEcpay.value = false;
      }
    };

    watch(activeTab, (tab) => {
      if (tab === 'every8d' && every8dCredit.value == null && !loadingEvery8dCredit.value) {
        fetchEvery8dCredit();
      }
    });

    watch(
      () => formData.ecpay_mode,
      (mode) => {
        if (mode === 'sandbox') {
          applyPaymentSandboxDefaultsIfNeeded();
        }
      }
    );

    onMounted(async () => {
      await fetchSettings();
      applyPaymentSandboxDefaultsIfNeeded();
    });

    return {
      notificationEvents,
      pageTitle,
      loading,
      savingGeneral,
      savingEcpay,
      savingEvery8d,
      loadingEvery8dCredit,
      every8dCredit,
      every8dAllocated,
      every8dDispatchable,
      formatSmsPoints,
      showEvery8dPwd,
      fetchingRate,
      error,
      activeTab,
      showHashKey,
      showHashIV,
      showInvoiceHashKey,
      showInvoiceHashIV,
      suggestedReturnWebhookUrl,
      suggestedPeriodWebhookUrl,
      suggestedClientBackUrl,
      autoFillReturnUrlSuggested,
      autoFillNotifyUrlSuggested,
      autoFillBothWebhookRoutes,
      autoFillClientBackUrl,
      autoFillPaymentSandbox,
      autoFillInvoiceSandbox,
      formData,
      fetchSettings,
      fetchRateFromCbc,
      handleSaveGeneral,
      handleSaveEcpay,
      handleSaveEvery8d,
      fetchEvery8dCredit
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.notification-event { padding-top: 16px; border-top: 1px solid #e0e0e0; }
.form-group .notification-checkbox { display: flex; align-items: center; gap: 8px; }
.notification-checkbox input { width: 16px; height: 16px; }
.form-group .notification-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0 12px;
  font-weight: 500;
  cursor: pointer;
}

/* 頁籤列 */
.tabs-bar {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 24px;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  background: none;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  border-radius: 4px 4px 0 0;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.tab-btn:hover {
  color: #1976d2;
  background: #f0f6ff;
}

.tab-btn.active {
  color: #1976d2;
  border-bottom-color: #1976d2;
  background: #f0f6ff;
}

/* 設定卡片 */
.settings-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.form-section h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.sms-point-board {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 8px 0 16px;
}

.sms-point-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px;
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  background: #f7f9fc;
}

.sms-point-label {
  font-size: 13px;
  font-weight: 600;
  color: #5f6b7a;
}

.sms-point-value {
  font-size: 28px;
  line-height: 1.1;
  font-weight: 700;
  color: #1f2933;
}

.sms-point-card.is-dispatch {
  background: #e8f5e9;
  border-color: #81c784;
}

.sms-point-card.is-dispatch .sms-point-label {
  color: #2e7d32;
}

.sms-point-card.is-dispatch .sms-point-value {
  font-size: 36px;
  color: #1b5e20;
}

.sms-point-card.is-dispatch.is-empty {
  background: #fff4e5;
  border-color: #ffb74d;
}

.sms-point-card.is-dispatch.is-empty .sms-point-label,
.sms-point-card.is-dispatch.is-empty .sms-point-value {
  color: #e65100;
}

@media (max-width: 768px) {
  .sms-point-board {
    grid-template-columns: 1fr;
  }
}

.section-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.6;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
}

.required { color: #e74c3c; }

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.form-hint strong {
  color: #333;
  font-weight: 600;
}

.form-hint ul {
  margin: 8px 0 0 20px;
  padding: 0;
  list-style-type: disc;
}

.form-hint ul li {
  margin: 4px 0;
  line-height: 1.5;
}

.form-group-compact {
  margin-top: 4px;
}

.form-hint.block-hint {
  margin-top: 8px;
}

.rate-input-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.rate-input-row .form-control {
  flex: 1;
  min-width: 120px;
}

.btn-fetch-cbc { white-space: nowrap; }

.url-input-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.url-input-row .form-control { flex: 1; }

.btn-auto-gen {
  white-space: nowrap;
  padding: 8px 14px;
  font-size: 13px;
  border: 1px solid #aaa;
  border-radius: 4px;
  background: white;
  color: #555;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.btn-auto-gen:hover {
  background: #f0f0f0;
  color: #333;
}

.input-with-toggle {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-toggle .form-control { padding-right: 40px; }

.btn-toggle-visibility {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: #888;
  font-size: 18px;
  padding: 0;
  display: flex;
  align-items: center;
  line-height: 1;
}

.btn-toggle-visibility:hover { color: #333; }

.radio-group {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
  color: #333;
}

.radio-label input[type="radio"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.form-group code {
  background: #f4f4f4;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  color: #e74c3c;
}

.form-actions {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

/* 電子發票區塊 */
.invoice-section {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 2px dashed #e0e0e0;
}

.invoice-section h2 {
  font-size: 18px;
}

.toggle-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.switch input:checked + .slider {
  background: #1976d2;
}

.switch input:checked + .slider:before {
  transform: translateX(22px);
}

.info-box {
  background: #e3f2fd;
  border-left: 4px solid #1976d2;
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 13px;
  color: #1565c0;
  margin-bottom: 20px;
  line-height: 1.6;
}

.info-box code {
  background: rgba(25, 118, 210, 0.1);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 12px;
}

.ecpay-webhook-info .ecpay-webhook-info-title {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 10px 0;
  font-weight: 600;
  font-size: 14px;
  color: #0d47a1;
}

.ecpay-webhook-info ul {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}

.ecpay-webhook-info li {
  margin: 8px 0;
  line-height: 1.55;
}

.sandbox-autofill {
  margin-top: 10px;
}
</style>
